<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Użytkownicy</span>
        <h3 class="page-title">
          <template v-if="isEdit">Edycja użytkownika</template>
          <template v-else>Dodawanie użytkownika</template>
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/uzytkownicy')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          Wróć do listy
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          Zapisz zmiany
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs card>
          <!-- Dane ogólne -->
          <d-tab title="Dane ogólne" active>
            <h4 class="mb-3 pb-3 border-bottom">
              Dane ogólne
            </h4>

            <validation-observer ref="user-form" tag="form">
              <div class="form-group">
                <label for="name">
                  Imię i nazwisko:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="data.name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="email">
                  E-mail:
                </label>

                <validation-provider
                  rules="required|email"
                  name="email"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    type="email"
                    v-model="data.email" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label class="d-block">
                  Rodzaj konta
                </label>
                <d-form-radio inline v-model="data.type" value="ADMIN">
                  Administrator
                </d-form-radio>
                <d-form-radio inline v-model="data.type" value="WORKER">
                  Pracownik
                </d-form-radio>
              </div>

              <div class="form-group">
                <d-checkbox inline toggle-small v-model="data.active" toggle>
                  Konto aktywne?
                </d-checkbox>
              </div>

              <div
                v-if="data.type === 'WORKER'"
                class="form-group">
                <label for="name">
                  Numer osobowy:
                </label>

                <validation-provider
                  rules="required"
                  name="personal_number"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="data.personal_number" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div
                v-if="data.type === 'WORKER'"
                class="form-group">
                <d-checkbox inline toggle-small v-model="data.is_pattern" toggle>
                  Wykrajanie?
                </d-checkbox>
              </div>

              <div
                v-if="data.type === 'WORKER'"
                class="form-group">
                <d-checkbox inline toggle-small v-model="data.is_seamstress" toggle>
                  Szycie?
                </d-checkbox>
              </div>

              <div
                v-if="data.type === 'WORKER'"
                class="form-group">
                <label class="d-block">
                  Rodzaj pracy
                </label>

                <validation-provider
                  rules="required"
                  name="work_type"
                  v-slot="{ errors }">
                  <d-form-radio
                    inline
                    v-model="data.work_type"
                    :state="errors.length ? 'invalid' : null"
                    value="PLANT">
                    Zakład
                  </d-form-radio>
                  <d-form-radio
                    inline
                    v-model="data.work_type"
                    :state="errors.length ? 'invalid' : null"
                    value="HOME">
                    Chałupnictwo
                  </d-form-radio>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div
                v-if="data.type === 'WORKER' && data.work_type === 'HOME'"
                class="form-group">
                <label for="name">
                  Adres:
                </label>

                <d-input v-model="data.work_address" />
              </div>

              <div
                v-if="data.type === 'WORKER' && data.work_type === 'HOME'"
                class="form-group">
                <label for="name">
                  Maszyna:
                </label>

                <d-input v-model="data.machine" />
              </div>

              <div class="form-group">
                <label for="email">
                  <template v-if="isEdit">Zmień hasło:</template>
                  <template v-else>Hasło:</template>
                </label>

                <validation-provider
                  :rules="isEdit ? 'min:6' : 'required|min:6'"
                  name="password"
                  v-slot="{ errors }">
                  <d-input
                    type="password"
                    :state="errors.length ? 'invalid' : null"
                    v-model="data.password" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="email">
                  Powtórz hasło:
                </label>

                <validation-provider
                  :rules="isEdit ? 'min:6|password:password' : 'required|min:6|password:password'"
                  name="password-confirmation"
                  v-slot="{ errors }">
                  <d-input
                    type="password"
                    :state="errors.length ? 'invalid' : null"
                    v-model="data.passwordConfirmation" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </validation-observer>

            <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center">
              Trwa ładowanie danych&hellip;
            </div>

            <d-alert
              v-if="loadError"
              show
              theme="warning">
              Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
            </d-alert>
          </d-tab>

          <d-tab :title="'Aktualne zamówienia (' + data.occupancies.length + ')'">
            <h4 class="mb-3 pb-3 border-bottom">
              Aktualne zamówienia
            </h4>

            <table
              v-if="data.occupancies.length"
              class="table w-100 mt-3 mb-0">
              <thead>
                <tr>
                  <th>Zamówienie</th>
                  <th>Produkt</th>
                  <th class="text-center" style="width: 180px;">Wykonano</th>
                  <th class="text-center" style="width: 180px;">Do wykonania</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="(task, index) of data.occupancies"
                  :key="'task-' + index">
                  <td class="align-middle">
                    {{ task.order_name }}
                  </td>
                  <td class="align-middle">
                    {{ task.product_name }}
                  </td>
                  <td class="align-middle text-center">
                    {{ task.quantity_done }} szt.
                  </td>
                  <td class="align-middle text-center">
                    {{ task.quantity }} szt.
                  </td>
                </tr>
              </tbody>
            </table>

            <d-alert
              v-if="!data.occupancies.length"
              show
              theme="warning">
              Nie znaleziono aktualnych zamówień dla tego pracownika.
            </d-alert>
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'user-form',
  computed: {
    allDataLoaded () {
      return this.loaded;
    },
    isEdit () {
      return !!this.$route.params.id;
    }
  },
  data() {
    return {
      data: {
        id: 0,
        name: '',
        email: '',
        type: 'ADMIN',
        work_type: 'PLANT',
        work_address: '',
        machine: '',
        is_pattern: false,
        is_seamstress: false,
        active: true,
        password: '',
        passwordConfirmation: '',
        personal_number: '',
        occupancies: []
      },
      loaded: false,
      loadError: false
    };
  },
  mounted () {
    if (this.isEdit) {
      this.loadItemData();
    } else {
      this.loaded = true;
    }
  },
  methods: {
    loadItemData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/users/item/',
        instanceFields: ['id', 'name', 'email', 'active', 'type', 'work_type', 'work_address', 'machine', 'personal_number', 'occupancies', 'is_pattern', 'is_seamstress'],
        booleanFields: ['active', 'is_pattern', 'is_seamstress'],
        loadedKey: 'loaded'
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['user-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = this.isEdit ? '/users/update/' + this.id : '/users/store';

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: 'Zapisano użytkownika',
        successText: 'Wybrany użytkownik został zapisany',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Modyfikacja użytkownika nie powiodła się. Spróbuj ponownie.',
        successAction: (response) => {
          if (!this.isEdit && response.item && response.item.id) {
            this.$router.push('/uzytkownicy/edycja/' + parseInt(response.item.id, 10));
          } else if (this.isEdit) {
            this.loaded = false;
            this.loadItemData();
          } else {
            this.$router.push('/uzytkownicy/');
          }
        }
      });
    },
    prepareFormDataToSend () {
      let formDataToSend = {
        email: this.data.email,
        name: this.data.name,
        active: this.data.active ? 1 : 0,
        type: this.data.type
      };

      if (this.data.type === 'WORKER') {
        formDataToSend.personal_number = this.data.personal_number;
        formDataToSend.work_type = this.data.work_type;
        formDataToSend.is_pattern = this.data.is_pattern;
        formDataToSend.is_seamstress = this.data.is_seamstress;
      }

      if (this.data.password !== '' && this.data.passwordConfirmation !== '') {
        formDataToSend.password = this.data.password;
        formDataToSend.password_confirmation = this.data.passwordConfirmation;
      }

      if (this.data.work_type === 'HOME') {
        formDataToSend.work_address = this.data.work_address;
        formDataToSend.machine = this.data.machine;
      }

      return formDataToSend;
    }
  }
};
</script>
